import { ArchivedBadge } from "@/components/ArchivedBadge";
import { Separator } from "@/modules/shadcn/ui/separator";
import { Model } from "@repo/schema";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BACKEND_LABELS, TRAINING_TYPE_VALUES } from "../../constants/labels";
import { AiSuggestionNotes } from "../AiSuggestionNotes";
import {
  getAugmentationById,
  getAugmentationSummary,
} from "../AugmentationSettings/augmentationTypes";
import {
  getPreprocessingById,
  getPreprocessingSummary,
} from "../PreprocessingSettings/preprocessingTypes";

export interface ModelParametersProps {
  model: Model;
}

const ParamItem = ({ label, value }: { label: string; value: string }) => (
  <div className="flex min-w-0 basis-45 grow flex-col gap-1">
    <span className="text-xs">{label}</span>
    <span className="text-sm font-bold break-words">{value}</span>
  </div>
);

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-3">
    <span className="text-[10px] font-bold uppercase tracking-wider">
      {title}
    </span>
    {children}
  </div>
);

export const ModelParameters = ({ model }: ModelParametersProps) => {
  const { t } = useTranslation(["model", "common"]);
  const [showHyperparams, setShowHyperparams] = useState(false);

  const hasCustomHyperparams =
    model.customHyperparams && Object.keys(model.customHyperparams).length > 0;

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-xl font-bold">{t("parameters.title")}</h2>
      {model.aiSuggestion && (
        <>
          <AiSuggestionNotes {...model.aiSuggestion} />
          <Separator />
        </>
      )}
      <Section title={t("parameters.sections.details")}>
        <div className="flex flex-wrap gap-3">
          <ParamItem label={t("fields.versionName")} value={model.name} />
          <ParamItem
            label={t("parameters.model")}
            value={t(BACKEND_LABELS[model.backend])}
          />
          <ParamItem label={t("fields.epochs")} value={String(model.epochs)} />
          <ParamItem
            label={t("fields.trainingType")}
            value={t(TRAINING_TYPE_VALUES[model.trainingType])}
          />
          <ParamItem
            label={t("fields.annotationsUsed")}
            value={model.annotationsUsed
              .map((type) => t(TRAINING_TYPE_VALUES[type]))
              .join(", ")}
          />
        </div>
      </Section>

      {model.labels.length > 0 && (
        <>
          <Separator />
          <Section title={t("fields.labels")}>
            <div className="flex flex-wrap gap-2">
              {model.labels.map((label) => (
                <div
                  key={label.id}
                  className="flex items-center gap-0 rounded-lg bg-black/[0.08] p-1"
                >
                  <div
                    className="h-6 w-2 shrink-0 rounded"
                    style={{ backgroundColor: label.color }}
                  />
                  <span className="px-2 text-xs">{label.name}</span>
                  {label.deletedAt && <ArchivedBadge className="mr-1.5" />}
                </div>
              ))}
            </div>
          </Section>
        </>
      )}

      <Separator />
      <Section title={t("split.title")}>
        <div className="flex flex-wrap gap-3">
          <ParamItem
            label={t("split.trainingSet")}
            value={`${model.splitTrain}%`}
          />
          <ParamItem
            label={t("split.validationSet")}
            value={`${model.splitValidate}%`}
          />
          <ParamItem
            label={t("split.testingSet")}
            value={`${model.splitTest}%`}
          />
        </div>
      </Section>

      {model.preprocessings.length > 0 && (
        <>
          <Separator />
          <Section title={t("preprocessing.title")}>
            <div className="flex flex-wrap gap-3">
              {model.preprocessings.map((pp) => {
                const def = getPreprocessingById(pp.type);
                const summary = def
                  ? getPreprocessingSummary(
                      def,
                      pp.params as Record<string, number | boolean | string>,
                      t,
                    )
                  : JSON.stringify(pp.params);
                const name = def ? t(def.name) : pp.type;
                const label = pp.keepOriginal
                  ? t("parameters.duplicated", { name })
                  : name;
                return (
                  <ParamItem key={pp.type} label={label} value={summary} />
                );
              })}
            </div>
          </Section>
        </>
      )}

      {model.augmentations.length > 0 && (
        <>
          <Separator />
          <Section title={t("parameters.sections.augmentations")}>
            <div className="flex flex-wrap gap-3">
              {model.augmentations.map((aug) => {
                const def = getAugmentationById(aug.type);
                const summary = def
                  ? getAugmentationSummary(
                      def,
                      aug.params as Record<string, number | boolean | string>,
                      t,
                    )
                  : JSON.stringify(aug.params);
                return (
                  <ParamItem
                    key={aug.type}
                    label={def ? t(def.name) : aug.type}
                    value={summary}
                  />
                );
              })}
            </div>
          </Section>
        </>
      )}

      <Separator />
      <Section title={t("parameters.sections.advanced")}>
        <div className="flex flex-wrap gap-3">
          <ParamItem
            label={t("fields.outputFormats")}
            value={model.outputTypes.join(", ")}
          />
          {hasCustomHyperparams && (
            <div className="flex min-w-0 basis-45 grow flex-col gap-1">
              <span className="text-xs">{t("fields.customHyperparams")}</span>
              <button
                type="button"
                className="cursor-pointer self-start border-none bg-none p-0 text-left text-sm font-bold text-emerald-700 underline"
                onClick={() => setShowHyperparams((prev) => !prev)}
              >
                {showHyperparams
                  ? t("parameters.hideHyperparams")
                  : t("parameters.showHyperparams")}
              </button>
            </div>
          )}
        </div>
        {showHyperparams && hasCustomHyperparams && (
          <div className="max-h-75 overflow-x-auto overflow-y-auto rounded-lg bg-black/4 p-3">
            <pre className="font-mono text-xs">
              {JSON.stringify(model.customHyperparams, null, 2)}
            </pre>
          </div>
        )}
      </Section>
    </div>
  );
};
